import type {
  CreateOneWorkerVersionRequestBodyType,
  CreateOneWorkerVersionResponseType,
  GetListWorkerFiltersType,
  GetListWorkerVersionFiltersType,
  UpdateOneWorkerVersionRequestBodyType,
  WorkerType,
  WorkerVersionType,
} from "../schemas/WorkerSchemas.ts";
import type {
  CreateOneRequestType,
  CreateOneResponseType,
  DeleteOneRequestType,
  DeleteOneResponseType,
  GetListRequestType,
  GetListResponseType,
  GetOneRequestType,
  GetOneResponseType,
  ResourceAdapterToAdapterQueryKeysType,
  ResourceAdapterType,
  UpdateOneRequestType,
  UpdateOneResponseType,
} from "../util/index.ts";
import { fetchAndCollate, getFilterObject, Resources } from "../util/index.ts";
import { restAPI } from "./api.ts";

export const WorkerAdapter: WorkerAdapterType = {
  /**
   * Search for workers
   * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workers/search_workers}
   */
  getList: ({ filters, page, pageSize, sortBy, sortDirection }) =>
    fetchAndCollate(
      ({ page, pageSize }) =>
        restAPI.nodeAPI.POST<GetListResponseType<WorkerType>>({
          endpoint: "/workers/search",
          body: {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_direction: sortDirection,
            filter: getFilterObject(filters),
          },
        }),
      page,
      pageSize
    ),
  /**
   * Get a worker
   * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workers/get_worker}
   */
  getOne: async ({ id }) => {
    const resp = await restAPI.nodeAPI.GET<WorkerType>({
      endpoint: `/workers/${id}`,
    });
    return { data: resp };
  },
  createOne: async ({ data }) => {
    const resp = await restAPI.nodeAPI.POST<WorkerType>({
      endpoint: "/workers",
      body: data,
    });
    return { data: resp };
  },
  /**
   * Delete a worker
   * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workers/delete_worker}
   */
  deleteOne: ({ id }: DeleteOneRequestType<WorkerType>) =>
    restAPI.nodeAPI.DELETE({ endpoint: `/workers/${id}` }),

  versions: {
    /**
     * Search for worker versions
     * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workers/search_worker_versions}
     */
    getList: ({ filters, page, pageSize, sortBy, sortDirection, meta }) =>
      fetchAndCollate(
        ({ page, pageSize }) =>
          restAPI.nodeAPI.POST<GetListResponseType<WorkerVersionType>>({
            endpoint: `/workers/${meta?.id}/versions/search`,
            body: {
              page,
              page_size: pageSize,
              sort_by: sortBy,
              sort_direction: sortDirection,
              filter: getFilterObject(filters),
            },
          }),
        page,
        pageSize
      ),
    /**
     * Get a worker version
     * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workers/get_worker_version}
     */
    getOne: async ({ id, meta }) => {
      const resp = await restAPI.nodeAPI.GET<WorkerVersionType>({
        endpoint: `/workers/${id}/versions/${meta?.version}`,
      });
      return { data: resp };
    },
    /**
     * Register a worker version and get credentials
     * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workers/register_worker_version}
     */
    createOne: async ({ data, meta }) => {
      const resp =
        await restAPI.nodeAPI.POST<CreateOneWorkerVersionResponseType>({
          endpoint: `/workers/${meta?.id}/versions`,
          body: data,
        });
      return { data: resp };
    },
    /**
     * Update worker version metadata
     * @see {@link https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workers/update_worker_version}
     */
    updateOne: async ({ id, data, meta }) => {
      const resp = await restAPI.nodeAPI.PUT<WorkerVersionType>({
        endpoint: `/workers/${id}/versions/${meta?.version}`,
        body: data,
      });
      return { data: resp };
    },
  },
};

export interface WorkerAdapterType
  extends Pick<
    ResourceAdapterType<WorkerType>,
    "getList" | "getOne" | "createOne" | "deleteOne"
  > {
  getList: (
    params: GetListRequestType<GetListWorkerFiltersType>
  ) => Promise<GetListResponseType<WorkerType>>;
  getOne: (
    params: GetOneRequestType<{ id: string }>
  ) => Promise<GetOneResponseType<WorkerType>>;
  createOne: (
    params: CreateOneRequestType<
      Pick<WorkerType, "name" | "description" | "machine_user_id">
    >
  ) => Promise<CreateOneResponseType<WorkerType>>;
  deleteOne: (
    params: DeleteOneRequestType<WorkerType>
  ) => Promise<DeleteOneResponseType>;
  versions: {
    getList: (
      args: GetListRequestType<GetListWorkerVersionFiltersType> & {
        meta: Pick<WorkerType, "id">;
      }
    ) => Promise<GetListResponseType<WorkerVersionType>>;
    getOne: (
      params: GetOneRequestType<Pick<WorkerType, "id">> & {
        meta: Pick<WorkerVersionType, "version">;
      }
    ) => Promise<GetOneResponseType<WorkerVersionType>>;
    createOne: (
      params: CreateOneRequestType<
        CreateOneWorkerVersionRequestBodyType,
        Pick<WorkerType, "id">
      >
    ) => Promise<CreateOneResponseType<CreateOneWorkerVersionResponseType>>;
    updateOne: (
      params: UpdateOneRequestType<
        UpdateOneWorkerVersionRequestBodyType,
        Pick<WorkerVersionType, "version">
      >
    ) => Promise<UpdateOneResponseType<WorkerVersionType>>;
  };
}

export const WorkerAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<WorkerAdapterType> =
  {
    getList: (params) => [Resources.WORKER, "getList", params] as const,
    getOne: (params) => [Resources.WORKER, "getOne", params.id] as const,
    createOne: (params) => [Resources.WORKER, "createOne", params] as const,
    deleteOne: (params) => [Resources.WORKER, "deleteOne", params.id] as const,
    versions: {
      getList: (params) =>
        [Resources.WORKER, "versions", "getList", params] as const,
      getOne: (params) =>
        [
          Resources.WORKER,
          "versions",
          "getOne",
          params.id,
          params.meta?.version,
        ] as const,
      createOne: (params) =>
        [Resources.WORKER, "versions", "createOne", params] as const,
      updateOne: (params) =>
        [
          Resources.WORKER,
          "versions",
          "updateOne",
          params.id,
          params.meta?.version,
          params.data,
        ] as const,
    },
  };
